import { isValidElement, useCallback, useEffect, useMemo, useState } from 'react';

import type { RenderableDynamicValues } from '../types';

import type { UseDictionaryService, UseDictionaryValue } from './types';

export const useDictionaryService: UseDictionaryService = (service, languageHandle, files, options) => {
  const [isReady, setIsReady] = useState(service.isReady);
  const [hasFailed, setHasFailed] = useState(service.hasFailed);

  useEffect(() => {
    if (!languageHandle || !files.length) {
      return;
    }
    service.update(languageHandle, files, options);
  }, [options, service, languageHandle, files]);

  useEffect(() => {
    setIsReady(service.isReady);
    return service.addEventListener('ready', (): void => setIsReady(service.isReady));
  }, [service]);

  useEffect(() => {
    setHasFailed(service.hasFailed);
    return service.addEventListener('fetch-failed', (): void => setHasFailed(service.hasFailed));
  }, [service]);

  const forceUpdate = useCallback(() => {
    service.update(languageHandle, files, options);
  }, [service, languageHandle, files, options]);

  return useMemo(() => ({ isReady, forceUpdate, hasFailed }), [isReady, forceUpdate, hasFailed]);
};

export const useDictionaryValue: UseDictionaryValue = (service, key, dynamicValues) => {
  const [value, setValue] = useState(service.getSafeValueByKey(key, dynamicValues));

  useEffect(() => {
    setValue(service.getSafeValueByKey(key, dynamicValues));
    return service.addEventListener('update', (): void => setValue(service.getSafeValueByKey(key, dynamicValues)));
  }, [service, key, dynamicValues]);

  return value;
};

export const useRenderableDynamicValues = (
  dynamicValues?: RenderableDynamicValues
): RenderableDynamicValues | undefined => {
  return useMemo(() => {
    if (dynamicValues) {
      if (typeof dynamicValues === 'function') {
        return dynamicValues;
      }
      if (Array.isArray(dynamicValues)) {
        if (dynamicValues.some((value) => typeof value === 'function' || isValidElement(value))) {
          return dynamicValues;
        }
      } else {
        const values = Object.values(dynamicValues);
        if (values.some((value) => typeof value === 'function' || isValidElement(value))) {
          return dynamicValues;
        }
      }
    }
  }, [dynamicValues]);
};
